import type { ChatProvider } from '#types/plugin/chat'

interface SierraConfig {
  cssPath?: string
  launcher?: boolean
}
interface SierraAPI {
  init: (config: any) => void
  cleanup: () => void
  openChatModal: () => void
  closeChatModal: () => void
  clearPersistedConversation: () => void
  endConversation: () => void
}

declare global {
  interface Window {
    sierra: SierraAPI
    sierraConfig: {
      waitForInit?: boolean
      customGreeting?: string
      onConversationStart?: () => void
      onClose?: () => void
      onOpen?: (opts: { isNewConversation: boolean }) => void
      variables?: {
        consumerId?: string
        isLoyaltyMember?: boolean
        locale: string
        brand: string
      }
    }
  }
}

export default (config?: SierraConfig): ChatProvider => {
  const { $feature, $gtm, $locale, $t } = useNuxtApp()
  const auth = useAuthStore()
  const user = useUserStore()
  const { path } = useRoute()
  const { brand } = useRuntimeConfig().public

  const { details, isLoyaltyMember } = storeToRefs(user)
  const { embedUrl } = $feature.configLivechat
  const isAvailable = ref(false)
  const sierraReady = ref(false)

  window.sierraConfig = {
    waitForInit: true
  }

  const loadCss = () => {
    const cssPath = config?.cssPath
    try {
      if (cssPath && !document.querySelector(`link[href="${cssPath}"]`)) {
        useHead({
          link: () => [
            {
              rel: 'stylesheet',
              type: 'text/css',
              href: cssPath,
              once: true
            }
          ],
        })
      }
    }
    catch (err) {
      console.warn('failed to load livechat css', err)
    }
  }

  const updateSierraWithUserData = (profileDetails) => {
    if (!profileDetails) return
    const userData = {
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value,
        locale: $locale,
        brand
      }
    }
    Object.assign(window.sierraConfig, userData)

    const firstName = profileDetails.firstName || ''
    if (auth.loggedIn) {
      window.sierraConfig.customGreeting = isLoyaltyMember.value
        ? replaceAll($t.sierraChatGreetingLoggedInAndLoyalty, {
            brandName: $t.brandName,
            firstName
          })
        : replaceAll($t.sierraChatGreetingLoggedInAndNotLoyalty, {
            firstName
          })
    }
    else {
      window.sierraConfig.customGreeting = $t.sierraChatGreeting
    }
  }

  const setSierraConfig = () => {
    const sierraConfig = {
      canEndConversation: true,
      chatInterfaceStrings: {
        disclosure: $t.sierraChatDisclaimer
      },
      customGreeting: $t.sierraChatGreeting,
      customStyle: { zIndex: '9999' },
      dimensions: {
        width: '25rem',
        height: '40rem'
      },
      display: 'corner',
      launcher: config?.launcher && { text: '', icon: 'oval' },
      titleBarLogoOnly: true
    }
    Object.assign(window.sierraConfig, sierraConfig)
  }

  const initializeLiveChatEventTracking = () => {
    window.sierraConfig.onOpen = () => $gtm?.push('livechat.onAction', 'Displayed', 'Sierra')
    window.sierraConfig.onConversationStart = () => {
      $gtm?.push('livechat.onAction', 'Started', 'Sierra')
      $gtm?.push('livechat.onAction', 'Agent Started', 'Sierra')
    }
    window.sierraConfig.onClose = () => $gtm?.push('livechat.onAction', 'Closed', 'Sierra')
  }

  const initSierra = () => {
    if (!window.sierra || !embedUrl) return

    window.sierra.cleanup?.()

    const config = {
      ...window.sierraConfig,
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value,
        locale: $locale,
        brand
      }
    }

    window.sierra.init(config)
    sierraReady.value = true
  }

  const isHomepage = computed(() => {
    const normalized = path.replace(/\/+$/, '')
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?)?$/.test(normalized)
  })

  const setSierraChat = () => {
    const isCookieAccepted = hasCookieConsent(['required', 'performanceAndAnalytics', 'functional', 'targeting'])
    try {
      if (!isCookieAccepted || !embedUrl) return

      loadCss()
      setSierraConfig()
      updateSierraWithUserData(details.value)
      initializeLiveChatEventTracking()

      const loadAndInit = () => {
        loadScript(embedUrl!, { type: 'module' }).then(() => {
          initSierra()
          isAvailable.value = true
        })
      }

      if (config?.launcher && ['en-ca', 'en-us', 'fr-ca'].includes($locale)) {
        if (isHomepage.value) {
          const waitForHideClass = () => {
            if (document.body.classList.contains('hide-chat-button')) {
              loadAndInit()
            }
            else {
              requestAnimationFrame(waitForHideClass)
            }
          }

          if (document.body.classList.contains('hide-chat-button')) {
            loadAndInit()
          }
          else {
            waitForHideClass()
          }
        }
        else {
          // Not homepage — load immediately
          loadAndInit()
        }
      }
      else {
        loadAndInit()
      }
    }
    catch (err) {
      console.error('Failed to load Sierra chat script:', err)
      isAvailable.value = false
    }
  }

  const openChat = () => {
    if (sierraReady.value && window.sierra) {
      window.sierra.openChatModal()
    }
    else {
      console.warn('Sierra chat not ready yet.')
    }
  }

  const closeChat = () => {
    if (window.sierra) {
      window.sierra.clearPersistedConversation()
      window.sierra.closeChatModal()
    }
  }

  watch(
    [() => auth.loggedIn, () => details.value],
    ([newAuthState, newDetails], [oldAuthState, oldDetails]) => {
      const authChanged = newAuthState !== oldAuthState
      const userChanged = newDetails !== oldDetails

      if (authChanged || userChanged) {
        updateSierraWithUserData(newDetails)
        initSierra()
      }

      // Only clear and close on logout
      if (authChanged && !newAuthState) {
        closeChat()
      }
    },
    { immediate: true }
  )

  setSierraChat()

  return {
    provide: {
      chat: {
        open: openChat,
        available: isAvailable
      }
    }
  }
}
